import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { errorToast, successToast, getFileType } from '../utils/helper';
import { createWorkoutPlan, updateWorkoutPlan } from '../api/healthAPI';

// Create the context
const WorkoutPlanContext = createContext();

// Custom hook to use the context
export const useWorkoutPlanCreation = () => {
  const context = useContext(WorkoutPlanContext);
  if (!context) {
    throw new Error('useWorkoutPlanCreation must be used within a WorkoutPlanContextProvider');
  }
  return context;
};

// Context provider component
export const WorkoutPlanContextProvider = ({ children }) => {
  // State for managing the workout plan creation process
  const [planData, setPlanData] = useState({
    // Details Tab
    plan_name: '',
    plan_description: '',
    workout_focus: '',
    location: '',
    difficulty: '',
    thumbnail_url: '',

    // Schedule Tab
    schedule: [],

    // Meta information
    workoutPlanId: null,
    currentTab: 'details',
    current_step: 1,
    status: 'draft',
    isLoading: false,
    errors: {}
  });

  // Update plan data
  const updatePlanData = useCallback((updates) => {
    setPlanData((prev) => ({
      ...prev,
      ...updates,
      errors: {} // Clear errors when updating data
    }));
  }, []);

  // Set errors
  const setErrors = useCallback((errors) => {
    setPlanData((prev) => ({
      ...prev,
      errors
    }));
  }, []);

  // Validate Details tab
  const validateDetailsTab = useCallback((data) => {
    const errors = {};

    if (!data.plan_name?.trim()) {
      errors.plan_name = 'Workout plan name is required';
    }

    if (!data.plan_description?.trim()) {
      errors.plan_description = 'Workout plan description is required';
    }

    if (!data.workout_focus) {
      errors.workout_focus = 'Workout focus is required';
    }

    if (!data.location) {
      errors.location = 'Workout location is required';
    }

    if (!data.difficulty) {
      errors.difficulty = 'Difficulty level is required';
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0
    };
  }, []);

  // Validate Schedule tab
  const validateScheduleTab = useCallback((data) => {
    const errors = {};

    // Schedule validation
    if (!data.schedule || data.schedule.length === 0) {
      errors.schedule = 'At least one workout day is required';
    } else {
      const hasEmptyDay = data.schedule.some((day) => !day.workouts || day.workouts.length === 0);
      if (hasEmptyDay) {
        errors.schedule = 'All workout days must have at least one workout assigned';
      }
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0
    };
  }, []);

  // Validate current tab
  const validateCurrentTab = useCallback(
    (tab) => {
      switch (tab) {
        case 'details':
          return validateDetailsTab(planData);
        case 'schedule':
          return validateScheduleTab(planData);
        default:
          return { errors: {}, isValid: true };
      }
    },
    [planData, validateDetailsTab, validateScheduleTab]
  );

  // Save details step (creates workout plan with draft status)
  const saveDetailsStep = useCallback(async () => {
    try {
      const isEditing = !!planData.workoutPlanId;
      console.log(isEditing ? 'Updating details:' : 'Creating workout plan:', planData);
      setPlanData((prev) => ({ ...prev, isLoading: true }));

      // Validate the details tab first
      const { errors, isValid } = validateDetailsTab(planData);
      if (!isValid) {
        setErrors(errors);
        throw new Error('Please fix the validation errors');
      }

      // Build payload for details step
      const payload = {
        plan_name: planData.plan_name,
        plan_description: planData.plan_description,
        workout_focus: planData.workout_focus,
        difficulty: planData.difficulty,
        location: planData.location,
        is_current_plan: false,
        is_active: true,
        current_step: 1,
        status: 'draft'
      };

      // Only include days array if creating new (not editing)
      // When editing, preserve existing days by not sending the field
      if (!isEditing) {
        payload.days = [];
      }

      // Add thumbnail file type if new file is selected for upload
      if (planData.thumbnailFile) {
        payload.thumbnail_url = getFileType(planData.thumbnailFile);
      }

      console.log(isEditing ? 'Updating' : 'Creating', 'workout plan with payload:', payload);

      // Call appropriate API
      const response = isEditing
        ? await updateWorkoutPlan(planData.workoutPlanId, payload)
        : await createWorkoutPlan(payload);

      if (response?.meta?.code === 1 && response?.data?._id) {
        // Upload thumbnail image if presigned URL is provided
        if (response?.meta?.thumbnailPreSignUrl?.uploadURL && planData.thumbnailFile) {
          await axios.put(response.meta.thumbnailPreSignUrl.uploadURL, planData.thumbnailFile, {
            headers: {
              'content-type': `${planData.thumbnailFile.type.split('/')[0]}/${
                planData.thumbnailFile.name.split('.')[1]
              }`
            }
          });
        }

        successToast('Details saved successfully!');

        // Update workoutPlanId and current_step
        setPlanData((prev) => ({
          ...prev,
          workoutPlanId: response.data._id,
          current_step: 2
        }));

        return {
          success: true,
          workoutPlanId: response.data._id,
          data: response.data
        };
      } else {
        throw new Error(
          response?.meta?.message || `Failed to ${isEditing ? 'update' : 'create'} workout plan`
        );
      }
    } catch (error) {
      console.error('Error saving details:', error);
      errorToast(error.message || 'Failed to save details');

      return {
        success: false,
        error: error.message
      };
    } finally {
      setPlanData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [planData, validateDetailsTab, setErrors]);

  // Save schedule step with status (publish or draft)
  const saveScheduleStep = useCallback(
    async (status = 'published') => {
      try {
        console.log('Saving schedule with status:', status);
        setPlanData((prev) => ({ ...prev, isLoading: true }));

        // Validate schedule tab
        const { errors, isValid } = validateScheduleTab(planData);
        if (!isValid) {
          setErrors(errors);
          throw new Error('Please add at least one workout to the schedule');
        }

        // Build final payload
        const payload = {
          plan_name: planData.plan_name,
          plan_description: planData.plan_description,
          workout_focus: planData.workout_focus,
          difficulty: planData.difficulty,
          location: planData.location,
          is_current_plan: false,
          is_active: status === 'published',
          current_step: 2,
          status: status,
          days: planData.schedule.map((day) => ({
            day_name: `Day ${day.day_number}`,
            day_sequence: day.day_number,
            workout_ids: day.workouts.map((workout) => workout._id)
          }))
        };

        // Add thumbnail file type if new file is selected
        if (planData.thumbnailFile) {
          payload.thumbnail_url = getFileType(planData.thumbnailFile);
        }

        console.log('Updating workout plan with schedule:', payload);

        // Always update when saving schedule (workoutPlanId should exist)
        if (!planData.workoutPlanId) {
          throw new Error('Workout plan ID not found. Please save details first.');
        }

        const response = await updateWorkoutPlan(planData.workoutPlanId, payload);

        if (response?.meta?.code === 1 && response?.data?._id) {
          // Upload thumbnail if changed
          if (response?.meta?.thumbnailPreSignUrl?.uploadURL && planData.thumbnailFile) {
            await axios.put(response.meta.thumbnailPreSignUrl.uploadURL, planData.thumbnailFile, {
              headers: {
                'content-type': `${planData.thumbnailFile.type.split('/')[0]}/${
                  planData.thumbnailFile.name.split('.')[1]
                }`
              }
            });
          }

          successToast(
            status === 'published'
              ? 'Workout plan published successfully!'
              : 'Workout plan saved as draft!'
          );

          return {
            success: true,
            workoutPlanId: response.data._id,
            data: response.data
          };
        } else {
          throw new Error(response?.meta?.message || 'Failed to save workout plan');
        }
      } catch (error) {
        console.error('Error saving schedule:', error);
        errorToast(error.message || 'Failed to save schedule');

        return {
          success: false,
          error: error.message
        };
      } finally {
        setPlanData((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [planData, validateScheduleTab, setErrors]
  );

  // Navigate to next tab
  const goToNextTab = useCallback(() => {
    setPlanData((prev) => ({
      ...prev,
      currentTab: prev.currentTab === 'details' ? 'schedule' : 'schedule'
    }));
  }, []);

  // Navigate to previous tab
  const goToPreviousTab = useCallback(() => {
    setPlanData((prev) => ({
      ...prev,
      currentTab: prev.currentTab === 'schedule' ? 'details' : 'details'
    }));
  }, []);

  // Set specific tab
  const setCurrentTab = useCallback((tab) => {
    setPlanData((prev) => ({
      ...prev,
      currentTab: tab
    }));
  }, []);

  // Context value
  const value = {
    planData,
    workoutPlanId: planData.workoutPlanId,
    currentTab: planData.currentTab,
    current_step: planData.current_step,
    isLoading: planData.isLoading,
    errors: planData.errors,
    updatePlanData,
    setErrors,
    saveDetailsStep,
    saveScheduleStep,
    goToNextTab,
    goToPreviousTab,
    setCurrentTab,
    validateDetailsTab,
    validateScheduleTab,
    validateCurrentTab
  };

  return <WorkoutPlanContext.Provider value={value}>{children}</WorkoutPlanContext.Provider>;
};
